import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CircularProgress, Grid, makeStyles, Tab, Tabs, Theme } from '@material-ui/core';
import { fetchAgentProjectById, ritzDataContext, selectLoggedInAgent, selectProjectAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchHomeKVStore, selectHomeKVStore, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { capitalizeAll, GenericMessageBox, FontIcon } from '@moderepo/mode-app-components';
import { modeUIContext, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { Agent, AgentRole } from '@moderepo/mode-internal-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import { DashboardContainer } from '.';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { Dashboard, HOME_DASHBOARDS_KV_KEY, isHomeDashboardsKeyValuePair, isProjectDashboardsSettings } from './dashboardTypes';
import { DashboardPreviewCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';



const useStyles = makeStyles((theme: Theme) => {
    return {

        root: {
        },

        tabIcon: {
            fontSize: 24,
            width   : 24,
            height  : 24,
        },

        dashboardTabsContainer: {
            width         : '100%',
            display       : 'flex',
            justifyContent: 'center',
            alignItems    : 'center',
        },

        dashboardsMenuToggler: {
            marginLeft: theme.spacing(2),
        },
    };
}, { name: 'DashboardsContainer' });



export interface DashboardsContainerProps {
    readonly projectId: number;

    /**
     * The context of which page the user is on. Because this component can be shown in different pages, on a Project page where there is no
     * user or home context or on a User's Home page which has user and home context. This data is not required but it helps create a better
     * URL when we take the user to another page.
     */
    readonly routeContext?: {
        readonly userId?: number | undefined;
        readonly homeId?: number | undefined;
    }
}



/**
 * This container contains multiple dashboards in 1 screen and provide the users some UI to switch dashboard
 * @returns
 */
export const DashboardsContainer: React.FC<DashboardsContainerProps> = (props: DashboardsContainerProps) => {

    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const classes = useStyles();
    const { trans } = useRitzTranslation(project);
    // Get the logged in Agent data for the given project. This is is not the same as loggedInAgent data.
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const canEditDashboard = projectAgent?.role === AgentRole.ADMIN;

    const [selectedDashboard, setSelectedDashboard] = useState<Dashboard>();


    // If there is a dashboardKV, get the Dashboards list from the dashboardKV
    const dashboards = useMemo(() => {
        if (props.routeContext?.homeId) {
            // Get dashboards settings from Home KV
            const dashboardKV = selectHomeKVStore(appDataState, props.routeContext.homeId, HOME_DASHBOARDS_KV_KEY);
            if (isHomeDashboardsKeyValuePair(dashboardKV)) {
                return dashboardKV.value;
            }
        } else if (project && isProjectDashboardsSettings(project.supportToolSettings.dashboardSettings)) {
            // Get dashboards settings from Project settings
            return project.supportToolSettings.dashboardSettings.dashboards;
        }

        return [];
    }, [appDataState, project, props.routeContext]);



    /**
     * This useEffect is used for fetching the Home KV store which is used for storing the provided homeId's dashboard
     */
    useEffect(() => {
        if (props.routeContext?.homeId) {
            appDataDispatch(fetchHomeKVStore(props.routeContext.homeId, HOME_DASHBOARDS_KV_KEY), true, true).catch(() => {
            // ignore error because appDataDispatch already handled the error
            });
        } else {
            dataDispatch(fetchAgentProjectById(loggedInAgent.id, props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [appDataDispatch, dataDispatch, loggedInAgent.id, props.projectId, props.routeContext]);



    /**
     * This useEffect will monitor the 'dashboards' array and make sure the selectedDashboard is valid. Because we use Tabs, we need to
     * make sure the selectedDashboard is one of the dashboard in dashboards array. Because dashboard can be deleted at anytime therefore
     * the selectedDashboard might not be valid sometime.
     */
    useEffect(() => {
        if (dashboards && dashboards.length > 0) {
            if (!selectedDashboard) {
                // if there is no dashboard selected, automatically select the first dashboard from the array
                setSelectedDashboard(dashboards[0]);
            } else if (
                dashboards.find((dashboard) => {
                    return dashboard.id === selectedDashboard.id;
                }) === undefined
            ) {
                // Else, make sure the selected dashboard exist. If not, also automatically select the first dashboard
                setSelectedDashboard(dashboards[0]);
            }
        }
    }, [dashboards, selectedDashboard]);


    /**
     * On create dashboard clicked, we will use the slide out panel to show the UI for creating a Dashboard
     */
    const onCreateDashboardClick = useCallback(() => {
        if (!canEditDashboard) {
            return;
        }
        const options: DashboardPreviewCompOptions = {
            compType : SlideOutPanelCompType.DASHBOARD_PREVIEW,
            location : SlideOutPanelLocation.RIGHT,
            compProps: {
                data: {
                    projectId: props.projectId,
                },
                routeContext: props.routeContext,
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [canEditDashboard, props.projectId, props.routeContext, uiDispatch]);



    return (
        <>
            {dashboards && (
                <Grid container alignItems="center" spacing={3}>

                    <Grid item xs={12}>
                        {dashboards.length > 0 && selectedDashboard ? (
                            <div className={classes.dashboardTabsContainer}>
                                <Tabs
                                    value={selectedDashboard.id}
                                    onChange={(event, dashboardId: number) => {
                                        const clickedDashboard = dashboards.find((dashboard) => {
                                            return dashboard.id === dashboardId;
                                        });
                                        setSelectedDashboard(clickedDashboard);
                                    }}
                                    indicatorColor="primary"
                                    textColor="primary"
                                    variant={dashboards.length > 0 ? 'scrollable' : 'standard'}
                                    scrollButtons="auto"
                                >
                                    {dashboards.map((dashboard) => {
                                        return (
                                            <Tab
                                                key={dashboard.id}
                                                value={dashboard.id}
                                                icon={dashboard.icon ? <FontIcon className={classes.tabIcon} iconName={dashboard.icon} /> : undefined}
                                                label={dashboard.title}
                                            />
                                        );
                                    })}
                                </Tabs>
                            </div>
                        ) : (
                            <GenericMessageBox
                                title={trans('generic.no_data.title', {
                                    term: capitalizeAll(trans('dashboard.label', { count: 2 })),
                                })}
                                action={canEditDashboard ? trans('dashboard.action.create_dashboard') : undefined}
                                actionHandler={() => {
                                    onCreateDashboardClick();
                                }}
                            />
                        )}
                    </Grid>


                    {selectedDashboard && (
                        <Grid item xs={12}>
                            <DashboardContainer
                                projectId={props.projectId}
                                dashboardId={selectedDashboard.id}
                                routeContext={props.routeContext}
                            />
                        </Grid>
                    )}
                </Grid>
            )}

            {!dashboards && (
                <CircularProgress />
            )}
        </>
    );
};



// Register this component with the RitzComponentsCollection so that it can be used by the Custom UI system
RitzComponentsCollection.getInstance().registerComponent('DashboardsContainer', DashboardsContainer);
